import React from "react"
import PropTypes from "prop-types"
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    LinearProgress
} from "@mui/material"
import { ExpandMore } from "@mui/icons-material"
import { plural } from "./datetime"

/**
 * A collapsible chapter in the course syllabus, Udemy style: the title, how
 * many lectures it holds and, for an enrolled student, how far they are.
 */
export default function ChapterSection({
    title,
    description,
    lectureCount,
    progress,
    defaultExpanded = false,
    children
}) {
    const percent = Math.round(Number(progress?.percent) || 0)

    return (
        <Accordion
            defaultExpanded={defaultExpanded}
            disableGutters
            elevation={0}
            sx={{
                border: "1px solid #e2e8f0",
                borderRadius: "16px !important",
                overflow: "hidden",
                "&::before": { display: "none" }
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{ bgcolor: "#f8fafc", px: { xs: 2, sm: 2.5 } }}
            >
                <div className="min-w-0 flex-1 pr-2">
                    <p className="text-sm font-semibold text-slate-800 sm:text-base">
                        {title}
                    </p>
                    <p className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-slate-500">
                        <span>{plural(lectureCount, "lecture")}</span>
                        {progress && (
                            <span className="font-medium text-emerald-700">
                                {progress.completed_count || 0}/
                                {progress.lecture_count || lectureCount} done
                            </span>
                        )}
                    </p>
                    {progress && (
                        <LinearProgress
                            variant="determinate"
                            value={percent}
                            sx={{
                                mt: 1,
                                maxWidth: 240,
                                height: 4,
                                borderRadius: 2,
                                bgcolor: "#e2e8f0",
                                "& .MuiLinearProgress-bar": { bgcolor: "#059669" }
                            }}
                        />
                    )}
                </div>
            </AccordionSummary>
            <AccordionDetails sx={{ p: { xs: 1.5, sm: 2 } }}>
                {description && (
                    <p className="mb-3 text-xs text-slate-500 sm:text-sm">
                        {description}
                    </p>
                )}
                {children}
            </AccordionDetails>
        </Accordion>
    )
}

ChapterSection.propTypes = {
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    lectureCount: PropTypes.number,
    progress: PropTypes.object,
    defaultExpanded: PropTypes.bool,
    children: PropTypes.node
}
